import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AlertCircleIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { FieldTypes } from 'constants/field-types-enum';
import { meetsLogic } from 'constants/logic';
import { componentMap, getComponentProps, handleLogicJump } from 'helpers/question-config';
import { useKeyboardHints } from 'hooks/keyboard-hints';
import i18n from 'i18n';
import { useTranslation } from 'react-i18next';

import {
  useCreateFormFieldTranslationsMutation,
  useLazyGetUiTranslationsPublicQuery,
} from 'api/forms-api';

import FormWrapper from '../Form/FormWrapper';

const dotToNested = flat =>
  Object.entries(flat).reduce((acc, [key, val]) => {
    key.split('.').reduce((obj, part, i, arr) => {
      if (i === arr.length - 1) obj[part] = val;
      else obj[part] = obj[part] || {};
      return obj[part];
    }, acc);
    return acc;
  }, {});

// Normalize answer value so meetsLogic gets a consistent shape (e.g. yes_no as "Yes"/"No").
const normalizeAnswerForRule = (question, value) => {
  if (value === null || value === undefined || value === '') return value;
  if (question?.type === FieldTypes.YesNo) {
    if (value === true || value === 'true' || value === 'Yes') return 'Yes';
    if (value === false || value === 'false' || value === 'No') return 'No';
  }
  return value;
};

// Check if a candidate's answer triggers a block rule for the current question.
// Returns the blocking rule if one matches, otherwise null.
// Rule.field from the API is the question's order (1-based). Match only by order to avoid false
// positives when question.id happens to equal another question's order.
const getBlockingRule = (rules, question, answerValue, allQuestions = []) => {
  if (!rules || !rules.length || !question) return null;
  const rawOrder = question.order;
  const questionOrder =
    rawOrder != null && rawOrder !== '' && !Number.isNaN(Number(rawOrder))
      ? Number(rawOrder)
      : null;
  const idx =
    questionOrder == null && allQuestions.length > 0
      ? allQuestions.findIndex(
          q => q && (q.id === question.id || q.id?.toString() === question.id?.toString()),
        )
      : -1;
  const orderFromIndex = idx >= 0 ? idx + 1 : null;
  const effectiveOrder = questionOrder ?? orderFromIndex;
  if (effectiveOrder == null) return null;

  const blockRules = rules.filter(r => {
    if (r.rule_type !== 'block') return false;
    if (!r.active) return false;
    const ruleField = Number(r.field);
    return ruleField === effectiveOrder;
  });
  const normalizedValue = normalizeAnswerForRule(question, answerValue);
  return blockRules.find(rule => meetsLogic(rule, normalizedValue)) || null;
};

const RefereeFormWrapper = ({
  formMessage,
  questions,
  logicJumps,
  rules = [],
  textParams = {},
  brand,
  candidateId,
  country,
  includeCandidateReferenceValidation = false,
  candidateEmail,
  candidateContact,
  contactCompareErrorMessage = '',
  emailCompareErrorMessage = '',
  getCandidateUpload,
  signCandidateUpload,
  createCandidateUpload,
  deleteCandidateUpload,
  onSubmit = () => {},
  locale = 'en_GB',
  token,
  formId = null,
  translationLanguageCode = null,
  uiTranslations = null,
  allowTranslation = false,
  hideLogo = false,
  height,
  hideHeader = false,
}) => {
  useTranslation();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [accumulatedEnhancedValues, setAccumulatedEnhancedValues] = useState({});
  const [createFormFieldTranslations] = useCreateFormFieldTranslationsMutation();
  const [getUiTranslations] = useLazyGetUiTranslationsPublicQuery();
  const originalLanguageRef = useRef(i18n.language);
  const [formCompleted, setFormCompleted] = useState(false);
  const [backward, setBackward] = useState(false);
  const [skippedQuestions, setSkippedQuestions] = useState(new Set());
  const [blockMessage, setBlockMessage] = useState(null);

  const [selectedLanguageCode, setSelectedLanguageCode] = useState(translationLanguageCode);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedTexts, setTranslatedTexts] = useState(() => {
    const map = {};
    (questions || []).forEach(q => {
      if (q.translated_text) map[q.id] = q.translated_text;
    });
    return map;
  });
  const [translatedDescriptions, setTranslatedDescriptions] = useState(() => {
    const map = {};
    (questions || []).forEach(q => {
      if (q.translated_description) map[q.id] = q.translated_description;
    });
    return map;
  });
  const [translatedOptions, setTranslatedOptions] = useState(() => {
    const map = {};
    (questions || []).forEach(q => {
      if (q.translated_options) map[q.id] = q.translated_options;
    });
    return map;
  });

  useEffect(() => {
    if (translationLanguageCode && uiTranslations) {
      i18n.addResourceBundle(
        translationLanguageCode,
        'translation',
        dotToNested(uiTranslations),
        true,
        true,
      );
      i18n.changeLanguage(translationLanguageCode);
    }
    return () => {
      i18n.changeLanguage(originalLanguageRef.current);
    };
  }, []);

  const handlers = {
    getCandidateUpload,
    signCandidateUpload,
    createCandidateUpload,
    deleteCandidateUpload,
  };

  const filteredQuestions = useMemo(() => {
    return questions.filter(q => !skippedQuestions.has(q.order));
  }, [questions, skippedQuestions]);

  const currentQuestion = filteredQuestions[currentQuestionIndex];
  const keyboardHint = useKeyboardHints(currentQuestion?.type);

  const handleNext = useCallback(
    async formValues => {
      setBackward(false);

      setAccumulatedEnhancedValues(prev => ({ ...prev, ...formValues }));

      // Check block rules before advancing
      const rawValue = formValues[currentQuestion.id.toString()];
      const currentQuestionValue =
        typeof rawValue === 'object' && rawValue !== null && 'value' in rawValue
          ? rawValue.value
          : rawValue;
      const blockRule = getBlockingRule(rules, currentQuestion, currentQuestionValue, questions);
      if (blockRule) {
        setBlockMessage(blockRule.message || 'You cannot proceed based on your answer.');
        return;
      }

      // Clear any previous block message
      setBlockMessage(null);

      if (currentQuestionIndex < filteredQuestions.length - 1) {
        const { jumped, newSkipped, jumpToIndex } = handleLogicJump(
          formValues,
          logicJumps,
          currentQuestion,
          currentQuestionValue,
          skippedQuestions,
          filteredQuestions,
        );

        const updatedFiltered = questions.filter(q => !newSkipped.has(q.order));

        setSkippedQuestions(newSkipped);

        if (jumped) {
          if (Number(jumpToIndex) === -1) {
            // "Finish form" jump: no question has order -1; complete the form immediately
            setFormCompleted(true);
            const filteredFormValues = Object.fromEntries(
              Object.entries(formValues).filter(([key]) =>
                updatedFiltered.some(q => q.id.toString() === key),
              ),
            );
            onSubmit(filteredFormValues);
          } else {
            const nextIndex = updatedFiltered.findIndex(q => q.order === jumpToIndex);
            setCurrentQuestionIndex(nextIndex !== -1 ? nextIndex : currentQuestionIndex + 1);
          }
        } else {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
      } else {
        setFormCompleted(true);
        const filteredFormValues = Object.fromEntries(
          Object.entries(formValues).filter(([key]) =>
            filteredQuestions.some(q => q.id.toString() === key),
          ),
        );
        onSubmit(filteredFormValues);
      }
    },
    [
      currentQuestionIndex,
      filteredQuestions,
      currentQuestion,
      logicJumps,
      onSubmit,
      rules,
      skippedQuestions,
      questions,
    ],
  );

  const handleBack = useCallback(() => {
    setBackward(true);
    setBlockMessage(null);
    if (currentQuestionIndex > 0) setCurrentQuestionIndex(currentQuestionIndex - 1);
  }, [currentQuestionIndex]);

  const handleLanguageChange = useCallback(
    async targetLanguageCode => {
      setSelectedLanguageCode(targetLanguageCode);
      if (!targetLanguageCode) {
        setTranslatedTexts({});
        setTranslatedOptions({});
        i18n.changeLanguage(originalLanguageRef.current);
        return;
      }
      setIsTranslating(true);
      try {
        const map = await createFormFieldTranslations({
          id: formId,
          language_code: targetLanguageCode,
        }).unwrap();
        const texts = {};
        const descs = {};
        const opts = {};
        Object.entries(map).forEach(([fieldId, data]) => {
          if (data.text) texts[fieldId] = data.text;
          if (data.translated_description) descs[fieldId] = data.translated_description;
          if (data.translated_options) opts[fieldId] = data.translated_options;
        });
        setTranslatedTexts(texts);
        setTranslatedDescriptions(descs);
        setTranslatedOptions(opts);
      } catch {}
      try {
        const uiStrings = await getUiTranslations(targetLanguageCode).unwrap();
        i18n.addResourceBundle(
          targetLanguageCode,
          'translation',
          dotToNested(uiStrings),
          true,
          true,
        );
        i18n.changeLanguage(targetLanguageCode);
      } catch {}
      setIsTranslating(false);
    },
    [formId, createFormFieldTranslations, getUiTranslations],
  );

  const currentResolvedQuestion = useMemo(() => {
    if (!currentQuestion) return null;
    const opts = translatedOptions[currentQuestion.id];
    if (!opts) return currentQuestion;
    return { ...currentQuestion, options: opts };
  }, [currentQuestion, translatedOptions]);

  const getQuestionProps = value => {
    const props = getComponentProps({
      question: currentResolvedQuestion || currentQuestion,
      value,
      candidateId,
      handlers,
      brand,
    });
    const question = currentResolvedQuestion || currentQuestion;
    if (question?.type === FieldTypes.PhoneNumber) {
      return {
        ...props,
        ...(country && { defaultCountry: country }),
        ...(includeCandidateReferenceValidation && {
          compareContacts: candidateContact,
          compareContactErrorMessage: contactCompareErrorMessage,
        }),
      };
    }
    if (question?.type === FieldTypes.Email && includeCandidateReferenceValidation) {
      return {
        ...props,
        compareEmailValidation: true,
        compareEmail: candidateEmail,
        compareErrorMessage: emailCompareErrorMessage,
      };
    }
    return props;
  };

  return (
    <div className={height === '100%' ? 'relative h-full' : 'relative'}>
      <FormWrapper
        question={currentResolvedQuestion || currentQuestion}
        questionIndex={currentQuestionIndex}
        totalQuestions={filteredQuestions.length}
        blockMessage={blockMessage}
        formMessage={formMessage}
        textParams={textParams}
        formCompleted={formCompleted}
        locale={locale}
        backward={backward}
        brand={brand}
        componentMap={componentMap}
        keyboardHint={keyboardHint}
        getComponentProps={getQuestionProps}
        onNext={handleNext}
        onBack={handleBack}
        allowTranslation={allowTranslation}
        accumulatedEnhancedValues={accumulatedEnhancedValues}
        selectedLanguageCode={selectedLanguageCode}
        translatedText={currentQuestion ? translatedTexts[currentQuestion.id] : null}
        translatedDescription={currentQuestion ? translatedDescriptions[currentQuestion.id] : null}
        translatedOptions={currentQuestion ? translatedOptions[currentQuestion.id] : null}
        onLanguageChange={handleLanguageChange}
        isTranslating={isTranslating}
        hideLogo={hideLogo}
        height={height}
        hideHeader={hideHeader}
      />
    </div>
  );
};

export default RefereeFormWrapper;
